package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.shadowings.gero.domain.model.ChangelogEntry
import com.shadowings.gero.presentation.util.rememberFeedbackDebugInfo
import com.shadowings.gero.util.FeedbackEmail
import com.shadowings.gero.util.FeedbackType
import com.shadowings.gero.util.rememberFeedbackSender
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.changelog_got_it
import gero.composeapp.generated.resources.changelog_send_feedback
import gero.composeapp.generated.resources.changelog_title
import gero.composeapp.generated.resources.feedback_update_issue_subject
import gero.composeapp.generated.resources.feedback_update_issue_template
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChangelogDialog(
    entry: ChangelogEntry,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // The platform default width leaves wide side margins on phones: let the
    // content's own 16dp padding be the only margin to the screen edge (capped
    // at Material's 560dp dialog width for tablets).
    BasicAlertDialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        ChangelogDialogContent(
            entry = entry,
            onDismiss = onDismiss,
            modifier = modifier.widthIn(max = 560.dp)
        )
    }
}

@Composable
fun ChangelogDialogContent(
    entry: ChangelogEntry,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val sendFeedback = rememberFeedbackSender()
    val feedbackDebugInfo = rememberFeedbackDebugInfo(FeedbackType.UPDATE_ISSUE)
    val feedbackSubject = stringResource(Res.string.feedback_update_issue_subject)
    val feedbackTemplate = stringResource(Res.string.feedback_update_issue_template)

    Surface(
        modifier = modifier.fillMaxWidth().padding(16.dp),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        tonalElevation = 6.dp
    ) {
        // The whole content scrolls, buttons included: on small phones a long
        // changelog must never push the dismiss button off screen.
        Column(
            modifier = Modifier
                .verticalScroll(rememberScrollState())
                .padding(24.dp)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(
                text = stringResource(Res.string.changelog_title, entry.version),
                style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                textAlign = TextAlign.Center
            )

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                entry.items.forEach { item ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = "•",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                        Text(
                            text = stringResource(item),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            }

            Button(
                onClick = onDismiss,
                modifier = Modifier
                    .fillMaxWidth()
                    .semantics { testTag = "changelog_btn_dismiss" }
            ) {
                Text(stringResource(Res.string.changelog_got_it))
            }

            TextButton(
                onClick = {
                    sendFeedback(
                        FeedbackEmail(
                            subject = feedbackSubject,
                            templateBody = feedbackTemplate,
                            debugInfo = feedbackDebugInfo
                        )
                    )
                },
                modifier = Modifier.semantics { testTag = "changelog_btn_feedback" }
            ) {
                Text(stringResource(Res.string.changelog_send_feedback))
            }
        }
    }
}
